import React from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, SlidersHorizontal } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  useReportControls, ColumnHeader, SearchDrawer, ColumnsToggle, rowMatchesFilters, applySort, type ColumnConfig,
} from '@/components/shared/report-controls';
import { fmtDateTime } from '@/lib/format-date';

interface SyncRunCounts {
  fetched: number; matched: number; updated: number; created: number; errors: number;
  ticks?: number; relinked?: number;
  /** Targeted runs only: selected assets the provider's feed did not list. */
  notInFeed?: number;
}
interface SyncRunListItem {
  id: string;
  provider: 'eroad' | 'blackhawk';
  status: 'running' | 'complete' | 'failed';
  /**
   * True for the scheduler's polling loop, folded into one row per provider
   * per 4-hour block. On those rows `fetched`/`matched`/`updated` are the last
   * tick's numbers while `created`/`errors`/`relinked` are block totals — see
   * sync-run.model.ts.
   */
  automatic?: boolean;
  startedAt: string;
  finishedAt: string | null;
  counts?: SyncRunCounts;
  triggeredBy?: { id?: string; fullName?: string; email?: string } | null;
  /** Targeted runs (Assets report "Sync from …" actions): the assets the run
   *  was limited to. Ids on the list endpoint, populated on the detail one.
   *  Empty or absent = the provider's whole fleet. */
  assetIds?: Array<string | TargetAsset>;
}
interface TargetAsset { id?: string; _id?: string; fleetNumber?: string; registrationNumber?: string | null }
interface CreatedAsset { id?: string; _id?: string; fleetNumber?: string; registrationNumber?: string | null; make?: string | null; model?: string | null; trackingProvider?: string | null }
interface SyncRunDetail extends SyncRunListItem {
  createdAssetIds?: CreatedAsset[];
  error?: string | null;
}

const PROVIDER_LABEL: Record<string, string> = { eroad: 'EROAD', blackhawk: 'Blackhawk' };

function StatusBadge({ status }: { status: 'running' | 'complete' | 'failed' }) {
  if (status === 'running') return <Badge variant="secondary">Running…</Badge>;
  if (status === 'failed') return <Badge variant="destructive">Failed</Badge>;
  return <Badge>Complete</Badge>;
}

function fmtDuration(start?: string, end?: string | null) {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 100) / 10;
  return `${s}s`;
}

export function SyncRunsListPage() {
  // Column config drives the shell. The list endpoint doesn't accept
  // `?<field>_contains=` style filters, so we filter/sort client-side after
  // fetch — bounded dataset (limit=50), so the cost is trivial.
  const columns: ColumnConfig[] = React.useMemo(() => [
    { key: 'startedAt', label: 'Started', filterType: 'date',
      render: (r: any) => fmtDateTime(r.startedAt) },
    { key: 'provider', label: 'Provider', filterType: 'enum',
      enumOptions: [
        { value: 'eroad', label: 'EROAD' },
        { value: 'blackhawk', label: 'Blackhawk' },
      ],
      render: (r: any) => PROVIDER_LABEL[r.provider] ?? r.provider },
    { key: 'status', label: 'Status', filterType: 'enum',
      enumOptions: [
        { value: 'running', label: 'Running' },
        { value: 'complete', label: 'Complete' },
        { value: 'failed', label: 'Failed' },
      ],
      render: (r: any) => <StatusBadge status={r.status} /> },
    { key: 'source', label: 'Source', filterType: 'enum',
      enumOptions: [
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'manual', label: 'Manual' },
      ],
      render: (r: any) => (r.automatic
        ? <Badge variant="secondary">Scheduled ×{r.counts?.ticks ?? 0}</Badge>
        : <Badge variant="outline">Manual</Badge>) },
    { key: 'scope', label: 'Scope', filterType: 'enum',
      enumOptions: [
        { value: 'fleet', label: 'Whole fleet' },
        { value: 'assets', label: 'Selected assets' },
      ],
      render: (r: any) => (r.assetIds?.length
        ? `${r.assetIds.length} asset${r.assetIds.length === 1 ? '' : 's'}`
        : 'Whole fleet') },
    { key: 'triggeredByName', label: 'By', filterType: 'text',
      sortField: 'triggeredByName',
      render: (r: any) => r.triggeredBy?.fullName || r.triggeredBy?.email || 'system' },
    { key: 'counts.fetched', label: 'Fetched', filterType: 'number',
      render: (r: any) => r.counts?.fetched ?? '—' },
    { key: 'counts.updated', label: 'Updated', filterType: 'number',
      render: (r: any) => r.counts?.updated ?? '—' },
    { key: 'counts.created', label: 'New', filterType: 'number',
      render: (r: any) => r.counts?.created ?? '—' },
    { key: 'counts.relinked', label: 'Re-linked', filterType: 'number',
      render: (r: any) => r.counts?.relinked || '—' },
    { key: 'counts.errors', label: 'Errors', filterType: 'number',
      render: (r: any) => r.counts?.errors ?? '—' },
    // A scheduled row spans a 4-hour block rather than a single call, so
    // start→finish would measure the gap between ticks and mean nothing.
    { key: 'duration', label: 'Duration',
      render: (r: any) => (r.automatic ? 'every 4h' : fmtDuration(r.startedAt, r.finishedAt)) },
  ], []);
  const ctl = useReportControls({ namespace: '/sync-runs', columns });

  const { data: allRows = [], isLoading } = useQuery({
    queryKey: ['/sync-runs', 'list'],
    queryFn: async () => (await api<SyncRunListItem[]>('/sync-runs?limit=50')).data || [],
  });

  // Project synthetic `triggeredByName` so the text column has something
  // concrete to match against — the row has a nested `triggeredBy.fullName`.
  const projected = React.useMemo(
    () => allRows.map((r) => ({
      ...r,
      triggeredByName: r.triggeredBy?.fullName || r.triggeredBy?.email || 'system',
      // Synthetic, so the Source column's enum filter has a scalar to match.
      source: r.automatic ? 'scheduled' : 'manual',
      scope: r.assetIds?.length ? 'assets' : 'fleet',
    })),
    [allRows],
  );
  const filtered = React.useMemo(
    () => projected.filter((r) => rowMatchesFilters(r, columns, ctl.filters)),
    [projected, columns, ctl.filters],
  );
  const sorted = React.useMemo(() => applySort(filtered, ctl.sortState), [filtered, ctl.sortState]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Sync Runs</h1>
        <p className="text-sm text-muted-foreground">Scheduled, whole-fleet and per-asset sync runs across all providers.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {ctl.activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" onClick={() => ctl.setFilters({})} className="text-xs">
            Clear filters ({ctl.activeFilterCount})
          </Button>
        )}
        {ctl.hasNonDefaultView && (
          <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={ctl.resetView}>
            Reset view
          </Button>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => ctl.setDrawerOpen(true)}>
          <SlidersHorizontal className="h-4 w-4 mr-1" /> Search
        </Button>
        <ColumnsToggle ctl={ctl} />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {ctl.visibleColumns.map((col) => (
                  <TableHead key={col.key} className="p-0">
                    <ColumnHeader {...ctl.headerProps(col)} />
                  </TableHead>
                ))}
                <TableHead className="w-24 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const colSpan = ctl.visibleColumns.length + 1;
                if (isLoading) return <TableRow><TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>;
                if (sorted.length === 0) return <TableRow><TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">{allRows.length === 0 ? 'No sync runs yet' : 'No runs match the current filters'}</TableCell></TableRow>;
                return null;
              })()}
              {sorted.map((r) => (
                <TableRow key={r.id}>
                  {ctl.visibleColumns.map((col) => (
                    <TableCell key={col.key}>
                      {col.render ? col.render(r) : (col.key.split('.').reduce((a: any, k) => a?.[k], r) ?? '—')}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <Link to={`/sync-runs/${r.id}`} className="text-primary hover:underline text-sm">Details</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {ctl.drawerOpen && <SearchDrawer {...ctl.drawerProps} />}
    </div>
  );
}

export function SyncRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  // While the run is still in flight the document mutates server-side; poll
  // every 2s until it lands in a terminal state, then fall back to staleTime.
  const { data, isLoading } = useQuery({
    queryKey: ['/sync-runs', id],
    queryFn: async () => (await api<SyncRunDetail>(`/sync-runs/${id}`)).data!,
    enabled: !!id,
    refetchInterval: (q) => {
      const status = (q.state.data as SyncRunDetail | undefined)?.status;
      return status === 'running' || status === undefined ? 2000 : false;
    },
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6">Sync run not found.</div>;

  const counts = data.counts ?? { fetched: 0, matched: 0, updated: 0, created: 0, errors: 0 };
  const created = (data.createdAssetIds ?? []) as CreatedAsset[];
  const targets = (data.assetIds ?? []) as TargetAsset[];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/sync-runs"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">
            {PROVIDER_LABEL[data.provider] ?? data.provider} Sync
            {targets.length > 0 && ` (${targets.length} asset${targets.length === 1 ? '' : 's'})`}
          </h1>
          <p className="text-xs text-muted-foreground">Run ID: {data.id}</p>
        </div>
        <div className="ml-auto"><StatusBadge status={data.status} /></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Fetched"   value={counts.fetched} />
        <Stat label="Matched"   value={counts.matched} />
        <Stat label="Updated"   value={counts.updated} />
        <Stat label="New"       value={counts.created} accent={counts.created > 0 ? 'success' : undefined} />
        <Stat label="Errors"    value={counts.errors}  accent={counts.errors > 0 ? 'destructive' : undefined} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Run details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <KV label="Triggered by" value={data.triggeredBy?.fullName || data.triggeredBy?.email || 'system'} />
          <KV label="Started"      value={fmtDateTime(data.startedAt)} />
          <KV label="Finished"     value={fmtDateTime(data.finishedAt)} />
          <KV label="Duration"     value={fmtDuration(data.startedAt, data.finishedAt)} />
          {targets.length > 0 && (
            <KV
              label="Assets synced"
              value={targets.map((a) => a.fleetNumber || a.registrationNumber || a.id || a._id).join(', ')}
            />
          )}
          {targets.length > 0 && (counts.notInFeed ?? 0) > 0 && (
            <KV
              label={`Not reported by ${PROVIDER_LABEL[data.provider] ?? data.provider}`}
              value={`${counts.notInFeed} asset${counts.notInFeed === 1 ? '' : 's'} missing from the provider's feed, so not updated`}
            />
          )}
          {data.error && <KV label="Error" value={data.error} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">New assets created</CardTitle>
            <Badge variant="secondary">{created.length}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Assets bootstrapped from this provider during the run. Open an asset to see its full change history.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {created.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground text-center">No new assets — every position matched an existing record.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fleet #</TableHead>
                  <TableHead>Rego</TableHead>
                  <TableHead>Make</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Provider</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {created.map((a) => {
                  const aid = a.id ?? a._id;
                  return (
                    <TableRow key={aid}>
                      <TableCell>
                        {aid ? <Link to="/fleet/assets" className="text-primary hover:underline">{a.fleetNumber || aid}</Link> : (a.fleetNumber || '—')}
                      </TableCell>
                      <TableCell>{a.registrationNumber || '—'}</TableCell>
                      <TableCell>{a.make || '—'}</TableCell>
                      <TableCell>{a.model || '—'}</TableCell>
                      <TableCell>{a.trackingProvider || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: 'success' | 'destructive' }) {
  const cls = accent === 'success' ? 'text-green-600 dark:text-green-400' : accent === 'destructive' ? 'text-destructive' : '';
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-2xl font-semibold ${cls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}
